import React, { useEffect, useState } from "react";
import { api, getSessionId } from "@/lib/api";
import { toast } from "sonner";
import { X, Send, Loader2, Copy, Check, Link2 } from "lucide-react";
import { track, EVENTS } from "@/lib/analytics";

const INCLUDE_OPTS = [
    ["behaviors", "Behavior summary (last 2 weeks)"],
    ["medications", "Medication list & allergies"],
    ["questions", "Saved questions for providers"],
    ["timeline", "Timeline of recent events"],
    ["wellness", "My wellness trend (optional — about me, not them)"],
];

export default function ShareUpdateDialog({ open, onClose, source = "care_team" }) {
    const sid = getSessionId();
    const [orgs, setOrgs] = useState([]);
    const [include, setInclude] = useState({ behaviors: true, medications: true, questions: true, timeline: false, wellness: false });
    const [recipient, setRecipient] = useState("link");
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if (!open) return;
        setResult(null);
        api.get("/care-access/mine").then(r => {
            const active = (r.data.connections || []).filter(c => c.status === "active");
            setOrgs(active);
            if (active.length) setRecipient(active[0].org_id);
        }).catch(() => setOrgs([]));
    }, [open]);

    if (!open) return null;

    const create = async () => {
        if (!Object.values(include).some(Boolean)) { toast("Pick at least one thing to share."); return; }
        setBusy(true);
        try {
            const r = await api.post(`/share-update/${sid}`, {
                include, timeline_days: 14,
                org_id: recipient === "link" ? null : recipient,
                note,
            });
            setResult(r.data);
            track(EVENTS.CARE_SUMMARY_SHARED, { method: recipient === "link" ? "secure_link" : "organization", source });
            toast.success(recipient === "link" ? "Secure link created — valid for 7 days." : "Update sent to your care team.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't share the update.");
        } finally { setBusy(false); }
    };

    const link = result?.token ? `${window.location.origin}/shared-update/${result.token}` : null;
    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true); setTimeout(() => setCopied(false), 2000);
            toast.success("Link copied.");
        } catch { toast.error("Couldn't copy."); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="share-update-dialog">
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />
            <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h2 className="text-lg font-heading font-semibold text-[#1F2624]">Share an update</h2>
                        <p className="text-xs text-[#6E7A74] mt-0.5">A one-time package — you choose exactly what's inside. It never changes your ongoing access settings.</p>
                    </div>
                    <button type="button" onClick={onClose} data-testid="share-update-close" className="text-[#6E7A74] hover:text-[#1F2624]"><X size={18} /></button>
                </div>

                {result ? (
                    <div className="mt-5" data-testid="share-update-result">
                        {link ? (
                            <>
                                <p className="text-sm text-[#4C5551]">Send this secure, view-only link to any provider. It expires in 7 days.</p>
                                <div className="mt-2.5 flex items-center gap-2 rounded-2xl border border-teal/30 bg-teal-soft/30 p-3">
                                    <Link2 size={14} className="text-teal-dark shrink-0" />
                                    <p className="text-xs text-teal-dark truncate flex-1">{link}</p>
                                    <button type="button" onClick={copyLink} data-testid="share-update-copy-link"
                                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-2xl text-xs font-semibold bg-white border border-cream-dark text-[#4C5551] hover:border-teal shrink-0">
                                        {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy"}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <p className="text-sm text-[#4C5551]">
                                Sent. <strong className="text-[#1F2624]">{result.org_name}</strong> will see this update in their workspace, and it's noted on your Timeline.
                            </p>
                        )}
                        <button type="button" onClick={onClose} className="ml-btn-primary text-sm mt-4" data-testid="share-update-done">Done</button>
                    </div>
                ) : (
                    <>
                        <div className="mt-4 space-y-2">
                            {INCLUDE_OPTS.map(([k, label]) => (
                                <label key={k} className="flex items-center gap-2.5 text-sm text-[#1F2624] cursor-pointer" data-testid={`share-include-${k}`}>
                                    <input type="checkbox" checked={!!include[k]}
                                        onChange={e => setInclude(i => ({ ...i, [k]: e.target.checked }))}
                                        className="w-4 h-4 accent-teal" />
                                    {label}
                                </label>
                            ))}
                        </div>
                        <div className="mt-4">
                            <label className="text-xs font-semibold text-[#4C5551]">Send to</label>
                            <select className="ml-input mt-1 !py-2 text-sm" value={recipient} onChange={e => setRecipient(e.target.value)} data-testid="share-update-recipient">
                                {orgs.map(o => <option key={o.org_id} value={o.org_id}>{o.org_name || "Organization"}</option>)}
                                <option value="link">Secure link (provider not on Memory Lane)</option>
                            </select>
                        </div>
                        <textarea rows={2} className="ml-input mt-3 text-sm !py-2" placeholder="Anything you want them to know? (optional)"
                            value={note} onChange={e => setNote(e.target.value)} data-testid="share-update-note" />
                        <button type="button" onClick={create} disabled={busy} className="ml-btn-primary text-sm mt-4" data-testid="share-update-send">
                            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Share update
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
